import { type ReactNode, useId } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

type Tone = 'default' | 'changed' | 'muted';

const statVariants = tv({
  slots: {
    root: 'flex flex-col gap-1 rounded-md border px-3 py-2.5',
    label: 'text-xs text-uniform-gray-500 dark:text-uniform-gray-400',
    value: 'text-lg font-semibold leading-6 text-uniform-gray-900 dark:text-uniform-gray-100',
    track: 'h-1 overflow-hidden rounded-full bg-uniform-gray-200 dark:bg-uniform-gray-700',
    bar: 'h-1 rounded-full bg-blue-600',
    sublabel: 'text-xs text-uniform-gray-500 dark:text-uniform-gray-400'
  },
  variants: {
    tone: {
      default: { root: 'border-uniform-gray-200 dark:border-uniform-gray-700' },
      changed: {
        root: 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30',
        sublabel: 'text-blue-800 dark:text-blue-300'
      },
      muted: {
        root: 'border-uniform-gray-200 opacity-55 dark:border-uniform-gray-700'
      }
    }
  },
  defaultVariants: { tone: 'default' }
});

type StatVariants = VariantProps<typeof statVariants>;

interface StatProps {
  label: ReactNode;
  value?: ReactNode | null;
  sublabel?: ReactNode;
  /** Fraction met, 0 to 1; renders a progress bar under the value. */
  progress?: number | null;
  tone?: Tone;
  /** The previous value; renders a "Changed · was …" sublabel with the changed tone. */
  changedFrom?: ReactNode;
  className?: string;
}

function Stat({ label, value, sublabel, progress, tone, changedFrom, className }: StatProps) {
  const resolvedTone = tone ?? (changedFrom != null ? 'changed' : 'default');
  const {
    root,
    label: labelCls,
    value: valueCls,
    track,
    bar,
    sublabel: sublabelCls
  } = statVariants({ tone: resolvedTone });

  const labelId = useId();
  const clampedProgress =
    progress == null ? null : Math.min(100, Math.max(0, Math.round(progress * 100)));

  return (
    <div className={cn(root(), className)}>
      <div id={labelId} className={labelCls()}>
        {label}
      </div>
      <div className={cn(valueCls(), value == null && 'text-uniform-gray-400')}>{value ?? '—'}</div>
      {clampedProgress != null && (
        <div
          className={track()}
          role="progressbar"
          aria-labelledby={labelId}
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}>
          <div className={bar()} style={{ width: `${clampedProgress}%` }} />
        </div>
      )}
      {changedFrom != null ? (
        <div className={sublabelCls()}>Changed · was {changedFrom}</div>
      ) : (
        sublabel != null && <div className={sublabelCls()}>{sublabel}</div>
      )}
    </div>
  );
}

export { Stat, statVariants };
export type { StatProps, StatVariants };
